import { useState, useRef } from "react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, Upload, Image, FileVideo, FileAudio, FileText, X, Check, AlertCircle, Plus } from "lucide-react";

interface UploadMediaFormProps {
  athleteId?: string;
  onSuccess?: () => void;
}

type FileStatus = "pending" | "uploading" | "done" | "error";

interface FileEntry {
  id: string;
  file: File;
  preview: string | null;
  mediaType: string;
  status: FileStatus;
  progress: number;
  errorMessage?: string;
}

// Wraps FileReader in a promise so multiple files can be read in sequence with await.
const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const titleFromFilename = (name: string) =>
  name.replace(/\.(heic|heif|jpg|jpeg|png|mp4|mov|webm)$/i, "");

const UploadMediaForm = ({ athleteId, onSuccess }: UploadMediaFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isPrivate, setIsPrivate] = useState(true);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [quotaHit, setQuotaHit] = useState(false);

  // Add tags (supports comma-separated input)
  const addTag = () => {
    if (tagInput.trim()) {
      // Split by comma and process each tag
      const newTags = tagInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !tags.includes(tag));

      if (newTags.length > 0) {
        setTags([...tags, ...newTags]);
      }
      setTagInput("");
    }
  };

  // Remove a tag
  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Handle Enter key in tag input
  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const removeFile = (id: string) => {
    setFileEntries((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry?.mediaType === "video" && entry.preview) URL.revokeObjectURL(entry.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const resetForm = () => {
    fileEntries.forEach((f) => {
      if (f.mediaType === "video" && f.preview) URL.revokeObjectURL(f.preview);
    });
    setFileEntries([]);
    setTitle("");
    setDescription("");
    setDate(new Date());
    setIsPrivate(true);
    setTags([]);
    setTagInput("");
  };

  // Handle file selection — supports selecting many photos/videos at once (e.g. a
  // whole weekend/tournament dump). Converts HEIC/HEIF (iPhone default) to JPEG
  // before upload so S3 always stores a web-safe format; Chrome on desktop can't
  // display HEIC natively.
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    // Reset the input so re-selecting a file removed from the batch works.
    e.target.value = "";

    setIsConverting(true);
    const newEntries: FileEntry[] = [];

    for (const file of incoming) {
      let fileToUse = file;

      // Only convert when the browser explicitly reports a HEIC/HEIF MIME type.
      // On iOS Safari the OS converts HEIC → JPEG automatically before handing
      // the file to the browser, so file.type is already "image/jpeg" even if
      // the filename ends in .heic. Running heic2any on a JPEG blob causes it to
      // throw — so we must NOT rely on the file extension here.
      const isHeic = file.type === "image/heic" || file.type === "image/heif";

      if (isHeic) {
        try {
          // Dynamic import keeps heic2any out of the main bundle — only loads when needed
          const heic2any = (await import("heic2any")).default;
          const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
          const convertedBlob = Array.isArray(result) ? result[0] : result;
          const jpegName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
          fileToUse = new File([convertedBlob], jpegName, { type: "image/jpeg" });
        } catch (err) {
          // heic2any is unreliable in mobile WebKit — fall back to uploading the
          // original file. The MediaItem display layer handles HEIC rendering, so
          // the photo will still appear correctly after upload.
          console.warn("[HEIC conversion] failed, uploading original:", err);
          fileToUse = file;
        }
      }

      let mediaType = "other";
      let preview: string | null = null;
      if (fileToUse.type.startsWith("image/")) {
        mediaType = "image";
        preview = await readFileAsDataURL(fileToUse);
      } else if (fileToUse.type.startsWith("video/")) {
        mediaType = "video";
        preview = URL.createObjectURL(fileToUse);
      } else if (fileToUse.type.startsWith("audio/")) {
        mediaType = "audio";
      } else if (fileToUse.type === "application/pdf") {
        mediaType = "pdf";
      }

      newEntries.push({
        id: crypto.randomUUID(),
        file: fileToUse,
        preview,
        mediaType,
        status: "pending",
        progress: 0,
      });
    }

    setIsConverting(false);
    const combined = [...fileEntries, ...newEntries];
    setFileEntries(combined);

    // Auto-set title to filename only when this is the single file in the batch.
    if (combined.length === 1 && !title) {
      setTitle(titleFromFilename(combined[0].file.name));
    }
  };

  // Upload mutation — uploads every pending file sequentially (sign → PUT to S3 →
  // finalize → save media record) so the server's storage-quota check, which sums
  // existing media each time, stays accurate across the batch.
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!athleteId) return null;
      const pending = fileEntries.filter((f) => f.status !== "done");
      if (pending.length === 0) return null;

      setIsUploading(true);

      let successCount = 0;
      const failures: { name: string; message: string }[] = [];
      let quotaExceeded = false;

      for (let i = 0; i < pending.length; i++) {
        const entry = pending[i];

        if (quotaExceeded) {
          setFileEntries((prev) =>
            prev.map((f) => (f.id === entry.id ? { ...f, status: "error", errorMessage: "Skipped — storage limit reached" } : f))
          );
          continue;
        }

        setFileEntries((prev) => (prev.map((f) => (f.id === entry.id ? { ...f, status: "uploading", progress: 0 } : f))));

        try {
          // Step 1: Get a presigned PUT URL from the server
          const ext = entry.file.name.includes(".") ? entry.file.name.split(".").pop()! : "bin";
          const signRes = await apiRequest("/api/uploads/sign", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contentType: entry.file.type,
              bytes: entry.file.size,
              ext,
            }),
          });
          if (!signRes.ok) {
            const err = await signRes.json().catch(() => ({}));
            const e = new Error(err.message || err.error || `Sign failed (${signRes.status})`);
            (e as any).code = err.error;
            throw e;
          }
          const { url: presignedUrl, key } = await signRes.json();

          // Step 2: PUT the file directly to S3 (no auth header -- presigned URL handles auth)
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener("progress", (event) => {
              if (event.lengthComputable) {
                const pct = Math.round((event.loaded / event.total) * 100);
                setFileEntries((prev) => prev.map((f) => (f.id === entry.id ? { ...f, progress: pct } : f)));
              }
            });
            xhr.addEventListener("load", () => {
              xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed (${xhr.status})`));
            });
            xhr.addEventListener("error", () => reject(new Error("Network error during S3 upload")));
            xhr.open("PUT", presignedUrl);
            xhr.setRequestHeader("Content-Type", entry.file.type);
            xhr.send(entry.file);
          });

          // Step 3: Finalize (optional analytics hook)
          apiRequest("/api/uploads/finalize", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key, bytes: entry.file.size, contentType: entry.file.type }),
          }).catch(() => {}); // non-blocking, ignore errors

          // Step 4: Save the media record in our database. The Title field is
          // shared across the batch; when uploading more than one file, each gets
          // a numbered suffix (e.g. "Regional Tournament 1") so they stay unique
          // and distinguishable in search and the gallery. Position is taken from
          // the full selected batch, not just the remaining pending files, so a
          // retry after a partial failure keeps each file's original number.
          const entryTitle =
            fileEntries.length > 1
              ? `${title} ${fileEntries.findIndex((f) => f.id === entry.id) + 1}`
              : title;
          const mediaRes = await apiRequest(`/api/athletes/${athleteId}/media`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              key,
              title: entryTitle,
              description,
              mediaType: entry.mediaType,
              date: date.toISOString(),
              tags,
              isPrivate,
              fileSize: entry.file.size,
            }),
          });
          if (!mediaRes.ok) {
            const err = await mediaRes.json().catch(() => ({}));
            const e = new Error(err.message || err.error || `Save failed (${mediaRes.status})`);
            (e as any).code = err.error;
            throw e;
          }

          setFileEntries((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: "done", progress: 100 } : f)));
          successCount++;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Upload failed";
          const code = (err as any)?.code;
          setFileEntries((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: "error", errorMessage: message } : f)));
          failures.push({ name: entry.file.name, message });
          if (code === "storage_limit_exceeded") quotaExceeded = true;
        }
      }

      return { successCount, failures, total: pending.length, quotaExceeded };
    },
    onSuccess: (result) => {
      if (!result) return;
      const { successCount, failures, total, quotaExceeded } = result;
      setQuotaHit(!!quotaExceeded);

      if (failures.length === 0) {
        toast({
          title: "Upload Successful",
          description: `${successCount} file${successCount !== 1 ? "s" : ""} uploaded successfully`,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/athletes", athleteId, "media"] });
        resetForm();
        if (onSuccess) onSuccess();
      } else if (successCount > 0) {
        toast({
          title: "Some Uploads Failed",
          description: `${successCount} of ${total} files uploaded. ${failures.length} failed — remove or retry them below.`,
          variant: "destructive",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/athletes", athleteId, "media"] });
        // Keep failed entries in the list so the user can retry or remove them; drop the succeeded ones.
        setFileEntries((prev) => prev.filter((f) => f.status !== "done"));
      } else {
        toast({
          title: "Upload Failed",
          description: failures[0]?.message || "Failed to upload media files",
          variant: "destructive",
        });
      }
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (fileEntries.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one file to upload",
        variant: "destructive"
      });
      return;
    }

    if (!title) {
      toast({
        title: "Error",
        description: "Please provide a title for the media",
        variant: "destructive"
      });
      return;
    }

    uploadMutation.mutate();
  };

  const getMediaIcon = (type: string) => {
    switch (type) {
      case "image":
        return <Image className="h-6 w-6 text-primary" />;
      case "video":
        return <FileVideo className="h-6 w-6 text-secondary" />;
      case "audio":
        return <FileAudio className="h-6 w-6 text-accent" />;
      case "pdf":
        return <FileText className="h-6 w-6 text-slate-100" />;
      default:
        return <Upload className="h-6 w-6 text-slate-300" />;
    }
  };

  const doneCount = fileEntries.filter((f) => f.status === "done").length;
  const uploadingEntry = fileEntries.find((f) => f.status === "uploading");
  const overallPercent = fileEntries.length
    ? Math.round(((doneCount + (uploadingEntry ? (uploadingEntry.progress || 0) / 100 : 0)) / fileEntries.length) * 100)
    : 0;

  const submitLabel = isConverting
    ? "Converting..."
    : isUploading
      ? `Uploading ${doneCount}/${fileEntries.length}...`
      : fileEntries.length > 1
        ? `Upload ${fileEntries.length} Files`
        : "Upload Media";

  return (
    <div className="space-y-4 text-slate-100">
      <h2 className="text-xl font-bold text-slate-100">Upload Media</h2>

      {quotaHit && (
        <div className="rounded-lg border border-[#E0A458]/40 bg-[#E0A458]/10 p-4">
          <p className="text-sm font-semibold text-slate-100">You've reached your plan's storage limit.</p>
          <p className="mt-1 text-sm text-slate-300">
            Upgrade for more space and no watermarks, so you never have to choose which memories to keep.
          </p>
          <Link href="/subscription">
            <div className="mt-3 inline-block cursor-pointer rounded-md bg-[#E0A458] px-4 py-2 text-sm font-semibold text-black hover:bg-[#f0b467]">
              See upgrade options
            </div>
          </Link>
        </div>
      )}

      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid grid-cols-2 rounded-md border border-[#415A77]/60 bg-[#0B1220] p-1">
          <TabsTrigger value="upload">Upload Files</TabsTrigger>
          <TabsTrigger value="record">Record Video/Audio</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload Area */}
            <div className="rounded-lg border-2 border-dashed border-[#415A77]/60 bg-[#0B1220] p-4">
              {isConverting ? (
                <div className="flex flex-col items-center py-6">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E0A458] border-t-transparent mb-3" />
                  <p className="text-slate-100 font-medium">Converting iPhone photo(s)...</p>
                  <p className="text-sm text-slate-400 mt-1">HEIC is being converted to JPEG</p>
                </div>
              ) : fileEntries.length === 0 ? (
                <div
                  className="cursor-pointer text-center py-6 transition-colors hover:bg-[#18233a] rounded-lg"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-100">
                    Drop your files here or click to browse
                  </p>
                  <p className="text-sm text-slate-300 mt-1">
                    Select multiple photos and videos to upload a whole weekend or tournament at once. Images, videos, audio, and PDF files supported (50MB max per file).
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {fileEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="relative aspect-square rounded-lg overflow-hidden border border-[#415A77]/60 bg-[#111827]"
                      >
                        {entry.mediaType === "image" && entry.preview ? (
                          <img src={entry.preview} alt={entry.file.name} className="h-full w-full object-cover" />
                        ) : entry.mediaType === "video" && entry.preview ? (
                          <video src={entry.preview} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2">
                            {getMediaIcon(entry.mediaType)}
                            <span className="text-[10px] text-slate-300 truncate w-full text-center">{entry.file.name}</span>
                          </div>
                        )}

                        {entry.mediaType === "video" && (
                          <div className="absolute bottom-1 left-1 rounded bg-black/70 p-0.5">
                            <FileVideo className="h-3 w-3 text-white" />
                          </div>
                        )}

                        {entry.status === "uploading" && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#E0A458] border-t-transparent" />
                          </div>
                        )}

                        {entry.status === "done" && (
                          <div className="absolute right-1 top-1 rounded-full bg-green-600 p-0.5">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}

                        {entry.status === "error" && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-red-950/85 p-1 text-center">
                            <AlertCircle className="h-4 w-4 text-red-300" />
                            <span className="text-[9px] text-red-200 line-clamp-2">{entry.errorMessage}</span>
                          </div>
                        )}

                        {entry.status !== "uploading" && entry.status !== "done" && (
                          <button
                            type="button"
                            onClick={() => removeFile(entry.id)}
                            className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 hover:bg-black/90"
                            aria-label={`Remove ${entry.file.name}`}
                          >
                            <X className="h-3 w-3 text-white" />
                          </button>
                        )}
                      </div>
                    ))}

                    {!isUploading && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-[#415A77]/60 bg-[#0B1220] text-slate-300 transition-colors hover:bg-[#18233a]"
                      >
                        <Plus className="h-6 w-6" />
                        <span className="text-xs">Add more</span>
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-slate-300 mt-3 text-center">
                    {fileEntries.length} file{fileEntries.length !== 1 ? "s" : ""} selected
                  </p>
                </>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,video/*,audio/*,application/pdf"
                multiple
              />
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">{fileEntries.length > 1 ? "Title (applies to all files)" : "Title"}</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={fileEntries.length > 1 ? "e.g. Regional Tournament" : "Enter a title for this media"}
                  required
                />
                {fileEntries.length > 1 && (
                  <p className="text-xs text-slate-400">
                    Files will be saved as &quot;{title.trim() || "Title"} 1&quot;, &quot;{title.trim() || "Title"} 2&quot;, etc.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      {date ? format(date, "PPP") : <span>Pick a date</span>}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { if (d) { setDate(d); setDatePickerOpen(false); } }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={fileEntries.length > 1 ? "Add details that apply to all selected files" : "Add details about this media"}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags (optional)</Label>
              <div className="flex">
                <Input
                  placeholder="Add tags separated by commas (e.g., Game, Practice, Tournament)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  className="flex-grow"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTag}
                  className="ml-2"
                >
                  Add
                </Button>
              </div>

              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="bg-primary/10 text-primary">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 rounded-full hover:bg-[#1B263B] p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="private"
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
              />
              <Label htmlFor="private">Private (only visible to you and The Loop)</Label>
            </div>

            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Upload Progress</Label>
                  <span>{doneCount} of {fileEntries.length} complete</span>
                </div>
                <div className="w-full bg-[#243247] h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300"
                    style={{ width: `${overallPercent}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="border-[#415A77] text-slate-300 hover:bg-[#1B263B] hover:text-white"
                onClick={() => {
                  if (onSuccess) onSuccess();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUploading || isConverting || fileEntries.length === 0}
                className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitLabel}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="record" className="pt-4">
          <div className="border border-[#415A77]/60 rounded-lg p-8 text-center bg-[#0B1220]">
            <div className="mb-4 mx-auto rounded-full bg-[#0B1220] p-4 inline-block">
              <FileVideo className="h-10 w-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium mb-2">Record Video or Audio</h3>
            <p className="text-slate-100 mb-4">
              Capture moments directly from your device's camera or microphone
            </p>
            <div className="flex justify-center space-x-4">
              <Button className="bg-secondary">
                <FileVideo className="h-4 w-4 mr-2" /> Record Video
              </Button>
              <Button variant="outline">
                <FileAudio className="h-4 w-4 mr-2" /> Record Audio
              </Button>
            </div>
            <p className="text-xs text-slate-300 mt-6">
              Note: Recording requires camera and microphone permissions
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UploadMediaForm;
